import { pipe, startsWith, endsWith } from 'ramda';
import { HTML_TAG } from '@fmpedia/enums';

function isString(val) {
  return typeof val === 'string' || val instanceof String
}

function isObject(val) {
  return typeof val === 'object' && !isArray(val) && val !== null
}

function isArray(val) {
  if (!val) return false

  return Array.isArray(val)
}

function hasParentWith(el, { className, attribute, id, refEl }) {
  return !!getParentWith(el, { className, attribute, id, refEl })
}

function getParentWith(el, { className, attribute, id, refEl }) {
  function getParent(el) {
    const targetEl = el.parentElement;

    if (!targetEl) {
      return false
    }

    const isFound =
      Array.from(targetEl.classList).includes(className) ||
      (targetEl.hasAttribute && targetEl.hasAttribute(attribute)) ||
      targetEl.id === id ||
      targetEl === refEl;

    return isFound ? targetEl : getParent(targetEl)
  }
  return getParent(el)
}

function focusOnElementByDataRef(dataRef) {
  const el = getElByDataRef(dataRef);
  if (!el || !el.focus || document.activeElement === el) return

  el.focus();
}

function getElByDataRef(ref, elementToSearchIn = null) {
  return (elementToSearchIn || document).querySelector(`[data-ref="${ref}"]`)
}

async function waitForElAppearsInDom({
  dataRef,
  selector,
  timeout = 5000,
  elementToSearchIn = null // not checked but may be useful
} = {}) {
  if (!dataRef && !selector) return null

  const timeoutPromise = new Promise((resolve) =>
    setTimeout(() => resolve(false), timeout)
  );
  const checkDomUpdatesPromise = new Promise((resolve) => {
    let isElementFound = false;

    const isElExistsInDomInitially = dataRef
      ? !!getElByDataRef(dataRef, elementToSearchIn)
      : (elementToSearchIn || document).querySelector(selector);

    if (isElExistsInDomInitially) {
      resolve(true);
    }

    const observer = new MutationObserver((mutationsList) => {
      let isElExistsInDom = false;

      for (const mutation of mutationsList) {
        if (mutation.type !== 'childList') return

        if (isElExistsInDom) break

        for (const addedNode of mutation.addedNodes) {
          const targetSelector = dataRef ? `[data-ref="${dataRef}"]` : selector;

          if (
            addedNode.parentNode &&
            addedNode.parentNode.querySelector &&
            addedNode.parentNode.querySelector(targetSelector)
          ) {
            isElExistsInDom = true;
            break
          }
        }
      }

      if (isElExistsInDom && !isElementFound) {
        observer.disconnect();
        isElementFound = true;
        resolve(true);
      }
    });
    observer.observe(elementToSearchIn || document.body, {
      childList: true,
      subtree: true
    });
  });

  return Promise.race([timeoutPromise, checkDomUpdatesPromise])
}

function valuesToLowercase(val) {
  if (isString(val)) {
    return val.toLowerCase()
  }

  if (isObject(val)) {
    return Object.entries(val).reduce((acc, [key, objVal]) => {
      return {
        ...acc,
        [key]: valuesToLowercase(objVal)
      }
    }, {})
  }

  if (isArray(val)) {
    return val.map((item) => valuesToLowercase(item))
  }

  return val
}

function getBinaryStringFromBooleanArray(arr) {
  return arr.reduce((acc, value) => acc + +!!value, '')
}

function checkIfFunction(val) {
  return val && (typeof val === 'function' || val instanceof Function)
}

/**
 * This function replace Object key names by another names
 * Implemented only for the 1st level (no deep implementation)
 * @param obj     - Object to remap
 * @param keyMap  - Object map where key - old key name, value - new key name
 * @returns { Object }
 */
function getRemapForNestedField(remap, parentKey) {
  const keyPart = `${parentKey}.`;
  return Object.entries(remap).reduce((acc, [key, value]) => {
    if (key.indexOf(keyPart) === 0) {
      return {
        ...acc,
        [key.split(keyPart)[1]]: value
      }
    }
    return acc
  }, {})
}

function remapObjectKeys(original, remap, modifier = (v) => v) {
  if (isArray(original)) {
    return original.map((v) => remapObjectKeys(v, remap, modifier))
  }

  if (isObject(original)) {
    return Object.entries(original).reduce((acc, [key, value]) => {
      const newValue =
        isObject(value) || isArray(value)
          ? remapObjectKeys(value, getRemapForNestedField(remap, key), modifier)
          : value;
      const newKey = remap[key] || modifier(key);
      return {
        ...acc,
        [newKey]: newValue
      }
    }, {})
  }

  return original
}

function generateHandlersBySettings(handlersSettings, element) {
  return handlersSettings
    .filter(({ conditionFn }) => conditionFn(element))
    .map(({ handlerFn }) => handlerFn)
}

/**
 * This function returns another function which is used to handle parsed HTML
 * elements according to the handlersSettings
 *
 * @param {Object[]}    handlersSettings  - Array of handler settings which should be applied to parsed elements
 * @param {Function}    handlersSettings[].conditionFn  - function which receives parsed element and return TRUE if the element should be processed by the handler
 * @param {Function}    handlersSettings[].handlerFn    - handler which modifies parsed element according to the business logic
 *
 * @returns {Function}  Handler for parsed elements which contains all handlers according to the provided settings
 */
function generateParsedDomElementsHandlerDeep(handlersSettings) {
  return function processElements(elements) {
    if (!elements) return elements

    if (Array.isArray(elements)) {
      /**
       * Notes:
       * - We perform flat() because processElements may return either handled element
       * or array of elements (i.e. dewrap the tag - we return array of it's content)
       * - We perform filter() because processElements may return null after handling the
       * element (i.e. remove some tag)
       *
       * Summary: element handler receives an element as an input and may return:
       * - null
       * - element
       * - array of elements
       * Therefore we can create very flexible handlers.
       */
      return elements
        .map(processElements)
        .flat()
        .filter((el) => !!el)
    }

    const handlers = generateHandlersBySettings(handlersSettings, elements);
    const updatedEl = handlers.length ? pipe(...handlers)(elements) : elements;

    if (!updatedEl) return updatedEl

    if (Array.isArray(updatedEl)) {
      return processElements(updatedEl)
    }

    if (isObject(updatedEl)) {
      return {
        ...updatedEl,
        content: processElements(updatedEl.content)
      }
    }

    return updatedEl
  }
}

/**
 * Used to find if some Object/Array (not primitive) has some parent in the provided
 * data structure (checked by function)
 *
 * @param dataStructure     - any data structure with Objects, Arrays or primitives
 * @param targetElement     - Object or Array (not primitive)
 * @param parentConditionFn - function to check parent item for condition
 * @returns {boolean|*}     - returns true if targetElement has at least one parent item
 *                            which matches the condition
 */
function hasParentInDataStructureDeep({
  dataStructure,
  targetElement,
  parentConditionFn
}) {
  if (!targetElement || !parentConditionFn) return false

  function hasEl(elements, elToFind) {
    if (!elements) {
      return { isTargetElementFound: false, parentCondition: false }
    }

    if (Array.isArray(elements)) {
      return elements.reduce(
        (acc, element) => {
          if (acc.isTargetElementFound && acc.parentCondition) return acc

          const { isTargetElementFound, parentCondition } = hasEl(
            element,
            elToFind
          );
          if (isTargetElementFound && parentCondition) {
            return { isTargetElementFound, parentCondition }
          }
          if (isTargetElementFound) {
            return {
              isTargetElementFound,
              parentCondition: parentConditionFn(element)
            }
          }
          return acc
        },
        { isTargetElementFound: false, parentCondition: false }
      )
    }

    if (isObject(elements)) {
      if (elements === elToFind) {
        return { isTargetElementFound: true, parentCondition: false }
      }

      if (elements.content) {
        return hasEl(elements.content, elToFind)
      }

      return { isTargetElementFound: false, parentCondition: false }
    }

    return { isTargetElementFound: false, parentCondition: false }
  }

  const { isTargetElementFound, parentCondition } = hasEl(
    dataStructure,
    targetElement
  );

  if (!isTargetElementFound) {
    console.log(
      'hasParentInDataStructureDeep():\nElement was not found in the provided data structure'
    );
    return false
  }

  return parentCondition
}

/**
 * Recursively searches for the first focusable element within a given DOM tree.
 * A focusable element is determined based on specific attributes and types, such as inputs,
 * buttons, textareas, links with href attributes, and any element with a positive tabIndex.
 *
 * The function conducts a depth-first search to ensure the first logically focusable element
 * in document order is returned. This is particularly useful for ensuring accessibility standards
 * are met by identifying the initial interaction point for keyboard and screen reader users.
 *
 * @param {Element} element - The root element from which the search for a focusable descendant begins.
 * @returns {Element|null} The first focusable element found within the given element's descendants,
 * or null if no focusable element is found.
 */
function getFirstFocusableDescendant(element) {
  if (!element || !element.childNodes) return null

  for (let i = 0; i < element.childNodes.length; i++) {
    const child = element.childNodes[i];
    const focusable = isFocusable(child)
      ? child
      : getFirstFocusableDescendant(child);

    if (focusable) {
      return focusable
    }
  }
  return null
}

function isFocusable(element) {
  if (element.tabIndex < 0) {
    return false
  }

  if (element.disabled) {
    return false
  }

  const nodeName = element.nodeName && element.nodeName.toLowerCase();

  switch (nodeName) {
    case HTML_TAG.ANCHOR:
      return !!element.href && element.rel !== 'ignore'
    case HTML_TAG.INPUT:
      return element.type !== 'hidden'
    case HTML_TAG.BUTTON:
    case HTML_TAG.SELECT:
    case HTML_TAG.TEXTAREA:
      return true
    default:
      return element.hasAttribute && element.hasAttribute('tabindex')
  }
}

function getWordCount(text) {
  if (!text) return 0

  const delimiters = /[.,:;?!]/gi;
  const oneOrMoreSpaces = / {2,}/gi;

  return text
    .replace(delimiters, ' ')
    .replace(oneOrMoreSpaces, ' ')
    .replace(/[\r\n]+/gi, ' ')
    .split(/\s/)
    .filter(Boolean).length
}

function addStartingSlash(str = '') {
  if (!str) return ''

  return startsWith('/', str) ? str : `/${str}`
}

function addEndingSlash(str = '') {
  if (!str) return ''

  return endsWith('/', str) ? str : `${str}/`
}

function removeEndingSlashes(str = '') {
  if (!str) return ''

  return str.replace(/\/+$/, '')
}

function removeStartingSlash(str = '') {
  return startsWith('/', str) ? str.slice(1) : str
}

function wrapIntoSlashes(str = '') {
  if (!str) return ''

  return addStartingSlash(addEndingSlash(str))
}

function unwrapFromSlashes(str = '') {
  return removeStartingSlash(removeEndingSlashes(str))
}

/**
 * const arr = [ { a: 'a1', b: 'b1' }, { a: 'a2', b: 'b2' } ]
 * arrayOfObjectsToMapByKey({
 *   arrayOfObjects: arr,
 *   keyName: 'a'
 * }
 * Result:
 * { a1: { a: 'a1', b: 'b1' }, a2: { a: 'a2', b: 'b2' } }
 *
 * @param arrayOfObjects
 * @param keyName
 * @param keyHandler
 * @param valueHandler
 * @returns {*}
 */
function arrayOfObjectsToMapByKey({
  arrayOfObjects,
  keyName,
  keyHandler,
  valueHandler
}) {
  return arrayOfObjects.reduce((acc, item) => {
    const key = item[keyName];

    if (!key) return acc

    const handledKey = keyHandler ? keyHandler(key) : key;

    if (!handledKey) return acc

    const value = valueHandler ? valueHandler(item) : item;
    acc[handledKey] = value;

    return acc
  }, {})
}

export { addEndingSlash, addStartingSlash, arrayOfObjectsToMapByKey, checkIfFunction, focusOnElementByDataRef, generateParsedDomElementsHandlerDeep, getBinaryStringFromBooleanArray, getElByDataRef, getFirstFocusableDescendant, getParentWith, getWordCount, hasParentInDataStructureDeep, hasParentWith, isArray, isObject, isString, remapObjectKeys, removeEndingSlashes, removeStartingSlash, unwrapFromSlashes, valuesToLowercase, waitForElAppearsInDom, wrapIntoSlashes };
